import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGeneratorConfig } from '../hooks/useGeneratorConfig';
import { useCurrentOrg } from '../hooks/useCurrentOrg';
import { BrandingSection } from '../components/editor/BrandingSection';
import { StepsSection } from '../components/editor/StepsSection';
import { PricingSection } from '../components/editor/PricingSection';
import { LeadCaptureSection } from '../components/editor/LeadCaptureSection';
import { PublishPanel } from '../components/editor/PublishPanel';
import { IntegrationPanel } from '../components/editor/IntegrationPanel';
import { EditorPreview } from '../components/editor/EditorPreview';
import { getIndustryLabel } from '../lib/industries';
import { Icon, type IconName } from '../components/Icon';

export function GeneratorEditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { org } = useCurrentOrg();
    const {
        generator,
        config,
        loading,
        error,
        saving,
        publishing,
        isDirty,
        updateConfig,
        save,
        setPublished,
    } = useGeneratorConfig(id);

    const [publishOpen, setPublishOpen] = useState(false);
    const publishRef = useRef<HTMLDivElement>(null);
    const [integrateOpen, setIntegrateOpen] = useState(false);
    const integrateRef = useRef<HTMLDivElement>(null);

    type TabId = 'branding' | 'steps' | 'pricing' | 'leads';
    const [activeTab, setActiveTab] = useState<TabId>('branding');

    // Raccourci clavier Ctrl/Cmd+S pour sauvegarder
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (isDirty && !saving) void save();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isDirty, saving, save]);

    // Fermeture du popover de publication au clic extérieur + ESC
    useEffect(() => {
        if (!publishOpen) return;
        const onClick = (e: MouseEvent) => {
            if (publishRef.current && !publishRef.current.contains(e.target as Node)) {
                setPublishOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setPublishOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [publishOpen]);

    // Fermeture du popover d'intégration au clic extérieur + ESC
    useEffect(() => {
        if (!integrateOpen) return;
        const onClick = (e: MouseEvent) => {
            if (integrateRef.current && !integrateRef.current.contains(e.target as Node)) {
                setIntegrateOpen(false);
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIntegrateOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [integrateOpen]);

    if (loading) {
        return (
            <div className="app-loader">
                <div className="spinner" aria-hidden />
                <p>Chargement de l'éditeur…</p>
            </div>
        );
    }

    if (error || !generator || !config || !org) {
        return (
            <div className="editor-error">
                <h2>Impossible d'ouvrir cet éditeur</h2>
                <p>{error ?? 'Générateur introuvable.'}</p>
                <Link to="/dashboard" className="btn-primary">Retour au dashboard</Link>
            </div>
        );
    }

    return (
        <div className="editor-layout">
            <header className="editor-topbar">
                <div className="editor-topbar-left">
                    <button
                        type="button"
                        className="editor-back-btn"
                        onClick={() => navigate('/dashboard')}
                        aria-label="Retour au dashboard"
                        title="Retour au dashboard"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>

                    <div className="editor-topbar-sep" aria-hidden />

                    <div className="editor-topbar-identity">
                        <div className="editor-title">
                            <span className="editor-title-name">{generator.name}</span>
                            <span className="editor-title-sector">
                                {getIndustryLabel(generator.industry, generator.industry_custom)}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="editor-topbar-right">
                    {isDirty ? (
                        <span className="editor-dirty-pill">Modifications non enregistrées</span>
                    ) : (
                        <span className="editor-saved-pill">À jour</span>
                    )}
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => void save()}
                        disabled={!isDirty || saving}
                    >
                        <Icon name={saving ? 'loader' : 'save'} size={15} className={saving ? 'icon-spin' : undefined} />
                        {saving ? 'Sauvegarde…' : 'Enregistrer'}
                    </button>

                    <div className="pub-popover-root" ref={integrateRef}>
                        <button
                            type="button"
                            className="btn-ghost"
                            onClick={() => setIntegrateOpen((v) => !v)}
                            aria-expanded={integrateOpen}
                            aria-haspopup="dialog"
                            disabled={!generator.is_published}
                            title={
                                generator.is_published
                                    ? "Obtenir le code d'intégration"
                                    : 'Publiez votre générateur pour l\'intégrer'
                            }
                        >
                            <Icon name="code" size={15} />
                            Intégrer
                        </button>

                        {integrateOpen && (
                            <div className="pub-popover integ-popover" role="dialog" aria-label="Intégration">
                                <IntegrationPanel generator={generator} org={org} />
                            </div>
                        )}
                    </div>

                    <div className="pub-popover-root" ref={publishRef}>
                        <button
                            type="button"
                            className={`btn-primary${generator.is_published ? ' is-published' : ''}`}
                            onClick={() => setPublishOpen((v) => !v)}
                            aria-expanded={publishOpen}
                            aria-haspopup="dialog"
                        >
                            <Icon name="globe" size={15} />
                            {generator.is_published ? 'Publié' : 'Publier'}
                            <svg
                                width="12" height="12" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                                aria-hidden
                                className={`pub-chevron${publishOpen ? ' is-open' : ''}`}
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </button>

                        {publishOpen && (
                            <div className="pub-popover" role="dialog" aria-label="Publication">
                                <PublishPanel
                                    generator={generator}
                                    org={org}
                                    config={config}
                                    isDirty={isDirty}
                                    saving={saving}
                                    publishing={publishing}
                                    onPublishToggle={(next) => void setPublished(next)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div className="editor-split">
                <aside className="editor-panel">
                    {(() => {
                        const tabs: {
                            id: TabId;
                            icon: IconName;
                            label: string;
                            description: string;
                            badge?: { kind: 'count'; value: number } | { kind: 'dot'; on: boolean };
                        }[] = [
                            {
                                id: 'branding',
                                icon: 'palette',
                                label: 'Identité',
                                description: 'Nom affiché, logo, couleur d’accent, langue et devise.',
                            },
                            {
                                id: 'steps',
                                icon: 'layers',
                                label: 'Étapes & champs',
                                description: 'Structurez votre formulaire en étapes et ajoutez des champs.',
                                badge: { kind: 'count', value: config.steps.length },
                            },
                            {
                                id: 'pricing',
                                icon: 'price',
                                label: 'Tarification',
                                description: 'Prix de base, règles conditionnelles et mentions légales.',
                                badge:
                                    config.pricing.rules.length > 0
                                        ? { kind: 'count', value: config.pricing.rules.length }
                                        : undefined,
                            },
                            {
                                id: 'leads',
                                icon: 'users',
                                label: 'Captation de leads',
                                description: 'Formulaire de contact affiché après le résumé.',
                                badge: { kind: 'dot', on: !!config.leadCapture?.enabled },
                            },
                        ];
                        const current = tabs.find((t) => t.id === activeTab) ?? tabs[0];

                        return (
                            <>
                                <nav className="etab-nav" role="tablist" aria-label="Sections de configuration">
                                    {tabs.map((t) => {
                                        const active = activeTab === t.id;
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                role="tab"
                                                aria-selected={active}
                                                aria-controls={`etab-panel-${t.id}`}
                                                id={`etab-${t.id}`}
                                                className={`etab${active ? ' is-active' : ''}`}
                                                onClick={() => setActiveTab(t.id)}
                                                title={t.label}
                                            >
                                                <span className="etab-icon" aria-hidden>
                                                    <Icon name={t.icon} size={18} />
                                                </span>
                                                <span className="etab-label">{t.label}</span>
                                                {t.badge && t.badge.kind === 'count' && (
                                                    <span className="etab-count">{t.badge.value}</span>
                                                )}
                                                {t.badge && t.badge.kind === 'dot' && (
                                                    <span
                                                        className={`etab-dot${t.badge.on ? ' is-on' : ''}`}
                                                        aria-label={t.badge.on ? 'Activé' : 'Désactivé'}
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </nav>

                                <div
                                    id={`etab-panel-${current.id}`}
                                    role="tabpanel"
                                    aria-labelledby={`etab-${current.id}`}
                                    className="etab-content"
                                >
                                    <header className="etab-content-head">
                                        <span className="etab-content-icon" aria-hidden>
                                            <Icon name={current.icon} size={18} />
                                        </span>
                                        <div>
                                            <h2>{current.label}</h2>
                                            <p>{current.description}</p>
                                        </div>
                                    </header>

                                    <div className="etab-content-body">
                                        {activeTab === 'branding' && (
                                            <BrandingSection
                                                branding={config.branding}
                                                organizationId={org.id}
                                                generatorId={generator.id}
                                                onChange={updateConfig}
                                            />
                                        )}
                                        {activeTab === 'steps' && (
                                            <StepsSection config={config} onChange={updateConfig} />
                                        )}
                                        {activeTab === 'pricing' && (
                                            <PricingSection config={config} onChange={updateConfig} />
                                        )}
                                        {activeTab === 'leads' && (
                                            <LeadCaptureSection config={config} onChange={updateConfig} />
                                        )}
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </aside>

                <section className="editor-preview">
                    <EditorPreview
                        config={config}
                        previewTarget={activeTab === 'leads' ? 'summary' : 'form'}
                    />
                </section>
            </div>
        </div>
    );
}
